
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import { storeToRefs } from "pinia";
import { useVaultStore } from "@/stores/vault";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import {
  useWithdrawalRequestsStore,
  type WithdrawalRequestUI,
} from "@/stores/withdrawalRequests";
import WithdrawalForm, {
  type WithdrawalFormSubmitPayload,
  type WithdrawalFormValue,
} from "./WithdrawalForm.vue";
import type { WithdrawalStatus } from "@shared/entities/Vault/VaultInterfaces";

dayjs.extend(advancedFormat);
dayjs.extend(timezone);

interface DestinationRow {
  address: string;
  amountUsd?: number;
  amountBtc?: number;
  currency: "USD" | "BTC";
  note?: string;
}

type FeeSpeed = "slow" | "standard" | "fast";
type PendingStatus = WithdrawalStatus;
type ActionType =
  | "authorize"
  | "reject"
  | "cancel"
  | "reopen"
  | "calculate"
  | "send"
  | "edit"
  | "retry"
  | "delete";

type PendingItem = WithdrawalRequestUI & { canAuthorize?: boolean };

export default defineComponent({
  name: "pending-withdrawals",
  components: { WithdrawalForm },
  setup() {
    const store = useStore();
    const currentUser = computed(() => store.getters["auth/getUsernamePK"]);

    const vault = useVaultStore();
    const { summaryBtcPrice, summaryAvailable } = storeToRefs(vault);

    const withdrawalsStore = useWithdrawalRequestsStore();
    const { pendingRequests } = storeToRefs(withdrawalsStore);

    const items = computed<Array<PendingItem>>(() => pendingRequests.value);
    const expanded = ref<Record<string, boolean>>({});
    const statusLoading = ref<Record<string, boolean>>({});
    const actionLoading = ref<Record<string, ActionType | null>>({});
    const selectedSpeed = ref<Record<string, FeeSpeed>>({});
    const editingId = ref<string | null>(null);
    const editingValue = ref<WithdrawalFormValue | null>(null);

    const btcPrice = computed(() => summaryBtcPrice.value || 0);
    const availableBtc = computed(
      () => parseFloat(String(summaryAvailable.value ?? "0")) || 0
    );

    const canAuthorize = (item: PendingItem): boolean => {
      if (item.status !== "pending") return false;
      const user = currentUser.value;
      if (!user) return false;
      if (!item.allowedApprovers || item.allowedApprovers.length === 0)
        return false;
      return item.allowedApprovers.includes(user);
    };

    const canEdit = (item: PendingItem): boolean => {
      const user = currentUser.value;
      if (!user) return false;
      // Backend enforces canonical equality.
      // Here we check strict equality. Ideally matches.
      return (
        item.createdBy === user &&
        (item.status === "pending" ||
          item.status === "rejected" ||
          item.status === "canceled" ||
          item.status === "failed")
      );
    };

    const getApprovalStatus = (item: PendingItem) => {
      const required = item.requiredApprovals;
      const approvals = item.approvals || [];
      const count = approvals.length;
      const total = Number(required || 1);

      return {
        count,
        total,
        required,
        approvals,
      };
    };

    const isEditing = (id: string) => editingId.value === id;

    const toggleExpand = (id: string) => {
      expanded.value[id] = !expanded.value[id];
    };

    const isUpdating = (id: string) => statusLoading.value[id] === true;

    const setActionLoading = (id: string, action: ActionType) => {
      statusLoading.value[id] = true;
      actionLoading.value[id] = action;
    };

    const clearActionLoading = (id: string) => {
      statusLoading.value[id] = false;
      actionLoading.value[id] = null;
    };

    const isActionLoading = (id: string, action: ActionType) =>
      actionLoading.value[id] === action;

    const toFormValue = (item: PendingItem): WithdrawalFormValue => ({
      concept: item.concept || "",
      note: item.note || "",
      destinations: (item.destinations || []).map((dest, index) => ({
        id: `${item._id}-${index}`,
        address: dest.address || "",
        currency: dest.currency === "USD" ? "USD" : "BTC",
        amountBtc: dest.amountBtc,
        amountUsd: dest.amountUsd,
        note: dest.note || "",
      })),
    });

    const startEdit = (item: PendingItem) => {
      if (!canEdit(item)) return;
      editingId.value = item._id;
      editingValue.value = toFormValue(item);
      expanded.value[item._id] = true;
    };

    const cancelEdit = () => {
      editingId.value = null;
      editingValue.value = null;
    };

    const validateAddress = async (address: string): Promise<boolean> => {
      try {
        const validateFn = httpsCallable(functions, "Payments-isValidWallet");
        const res: any = await validateFn(address);
        const opcode = res?.data?.OPCODE || res?.OPCODE;
        const payload = res?.data ?? res;
        const validFlag =
          opcode === "SUCCESS"
            ? payload?.data?.valid ?? payload?.valid ?? true
            : payload?.valid ?? payload === true;
        return Boolean(validFlag);
      } catch (error) {
        console.error("Error validating address", error);
        return false;
      }
    };

    const saveEdit = async (
      id: string,
      payload: WithdrawalFormSubmitPayload
    ) => {
      const item = items.value.find((row) => row._id === id);
      if (!item || !isEditing(id) || isUpdating(id)) return;
      setActionLoading(id, "edit");
      try {
        const updateFn = httpsCallable(
          functions,
          "Vault-UpdateSystemWithdrawalRequest"
        );
        const response: any = await updateFn({
          requestId: id,
          concept: payload.concept,
          note: payload.note,
          destinations: payload.destinations,
        });
        const opcode = response?.data?.OPCODE || response?.OPCODE;
        if (opcode !== "SUCCESS") {
          const message =
            response?.data?.message || response?.message || "No actualizado";
          alert(message);
        } else {
          cancelEdit();
        }
      } catch (error: any) {
        console.error("Error updating withdrawal", error);
        alert(error?.message || "Error al actualizar retiro");
      } finally {
        clearActionLoading(id);
      }
    };

    const updateStatus = async (
      id: string,
      status: PendingStatus,
      action: ActionType,
      reason?: string
    ) => {
      if (isUpdating(id)) return;
      setActionLoading(id, action);
      try {
        const updateFn = httpsCallable(
          functions,
          "Vault-UpdateSystemWithdrawalRequestStatus"
        );
        const response: any = await updateFn({
          requestId: id,
          status,
          reason,
        });
        const opcode = response?.data?.OPCODE || response?.OPCODE;
        if (opcode !== "SUCCESS") {
          const message =
            response?.data?.message || response?.message || "No actualizado";
          alert(message);
        }
      } catch (error: any) {
        console.error("Error updating withdrawal status", error);
        alert(error?.message || "Error al actualizar estado");
      } finally {
        clearActionLoading(id);
      }
    };

    const authorize = (id: string) =>
      updateStatus(id, "authorized", "authorize");
    const retry = (id: string) => updateStatus(id, "authorized", "retry");
    const reject = (id: string) => updateStatus(id, "rejected", "reject");
    const cancel = (id: string) => updateStatus(id, "canceled", "cancel");
    const reopen = (id: string) => updateStatus(id, "pending", "reopen");
    const deleteReq = (id: string) => updateStatus(id, "deleted", "delete");

    const speedLabel = (speed: FeeSpeed) => {
      switch (speed) {
        case "slow":
          return "Lenta";
        case "fast":
          return "Rápida";
        default:
          return "Estándar";
      }
    };

    const calculateFee = async (id: string) => {
      const item = items.value.find((row) => row._id === id);
      if (!item || isUpdating(id)) return;
      const speed: FeeSpeed =
        selectedSpeed.value[item._id] ||
        (item.feeSpeed as FeeSpeed) ||
        "standard";
      setActionLoading(id, "calculate");
      try {
        const quoteFn = httpsCallable(functions, "Vault-QuoteTransaction");
        const response: any = await quoteFn({
          requestId: id,
          feeSpeed: speed,
        });
        const opcode = response?.data?.OPCODE || response?.OPCODE;
        if (opcode !== "SUCCESS") {
          const message =
            response?.data?.message ||
            response?.message ||
            "No se pudo cotizar";
          alert(message);
        }
      } catch (error: any) {
        console.error("Error quoting withdrawal", error);
        alert(error?.message || "Error al cotizar el retiro");
      } finally {
        clearActionLoading(id);
      }
    };

    const sendTransaction = async (id: string) => {
      const item = items.value.find((row) => row._id === id);
      if (!item || isUpdating(id)) return;
      setActionLoading(id, "send");
      try {
        const sendFn = httpsCallable(functions, "Vault-SendTransaction");
        const response: any = await sendFn({
          requestId: id,
        });
        const opcode = response?.data?.OPCODE || response?.OPCODE;
        if (opcode !== "SUCCESS") {
          const message =
            response?.data?.message || response?.message || "No se pudo enviar";
          alert(message);
        }
      } catch (error: any) {
        console.error("Error sending withdrawal", error);
        alert(error?.message || "Error al enviar el retiro");
      } finally {
        clearActionLoading(id);
      }
    };

    const statusLabel = (status: PendingStatus): string => {
      switch (status) {
        case "pending":
          return "Pendiente";
        case "authorized":
          return "Autorizado";
        case "ready":
          return "Para envío";
        case "rejected":
          return "Rechazado";
        case "canceled":
          return "Cancelado";
        case "failed":
          return "Fallida";
        default:
          return status;
      }
    };

    const statusClass = (status: PendingStatus): string => {
      switch (status) {
        case "pending":
          return "bg-warning";
        case "authorized":
          return "bg-info";
        case "ready":
          return "bg-success";
        case "rejected":
          return "bg-danger";
        case "canceled":
          return "bg-secondary";
        case "failed":
          return "bg-danger";
        default:
          return "bg-secondary";
      }
    };

    const statusIcon = (status: PendingStatus): string => {
      switch (status) {
        case "pending":
          return "far fa-clock";
        case "authorized":
          return "fas fa-thumbs-up";
        case "ready":
          return "fas fa-sign-out-alt";
        case "rejected":
          return "far fa-times-circle";
        case "canceled":
          return "fas fa-ban";
        case "failed":
          return "fas fa-exclamation-triangle";
        default:
          return "fas fa-info-circle";
      }
    };

    const formatDate = (date: Date) =>
      dayjs(date).format("YYYY/MM/DD HH:mm:ss z");

    const formatUsd = (value: number | undefined | null) => {
      const num = Number(value ?? 0);
      return Number.isFinite(num)
        ? num.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "0.00";
    };

    const formatBtc = (value: number | undefined | null) => {
      const num = Number(value ?? 0);
      return Number.isFinite(num) ? num.toFixed(8) : "0.00000000";
    };

    const feeUsd = (item: PendingItem) => item.feeUsd || 0;

    const amountUsdValue = (item: PendingItem, dest: DestinationRow) => {
      if (item.status === "ready") {
        const fromBtc = (dest.amountBtc || 0) * btcPrice.value;
        return dest.amountUsd ?? fromBtc;
      }
      if (dest.currency === "USD") return dest.amountUsd ?? 0;
      const price = btcPrice.value || 0;
      if (price > 0) return (dest.amountBtc || 0) * price;
      return dest.amountUsd ?? 0;
    };

    const amountBtcValue = (item: PendingItem, dest: DestinationRow) => {
      if (item.status === "ready") {
        const fromUsd = btcPrice.value
          ? (dest.amountUsd || 0) / btcPrice.value
          : 0;
        return dest.amountBtc ?? fromUsd;
      }
      if (dest.currency === "BTC") return dest.amountBtc ?? 0;
      const price = btcPrice.value || 0;
      if (price > 0) return (dest.amountUsd || 0) / price;
      return dest.amountBtc ?? 0;
    };

    const destinationsUsdTotal = (item: PendingItem) =>
      (item.destinations || []).reduce(
        (sum, dest) => sum + amountUsdValue(item, dest),
        0
      );

    const destinationsBtcTotal = (item: PendingItem) =>
      (item.destinations || []).reduce(
        (sum, dest) => sum + amountBtcValue(item, dest),
        0
      );

    const totalUsdWithFee = (item: PendingItem) => {
      const destinationsTotal = destinationsUsdTotal(item);
      const fee = feeUsd(item);
      const providedTotal = Number(item.totalUsd || 0);
      const baseTotal = providedTotal > 0 ? providedTotal : destinationsTotal;
      const includesFee =
        fee > 0 && providedTotal >= destinationsTotal + fee - 0.01;

      return includesFee ? baseTotal : destinationsTotal + fee;
    };

    const totalBtcWithFee = (item: PendingItem) => {
      const destinationsTotal = destinationsBtcTotal(item);
      const fee = item.feeBtc || 0;
      const providedTotal = Number(item.totalBtc || 0);
      const baseTotal = providedTotal > 0 ? providedTotal : destinationsTotal;
      const includesFee =
        fee > 0 && providedTotal >= destinationsTotal + fee - 1e-8;

      return includesFee ? baseTotal : destinationsTotal + fee;
    };

    const subtotalUsdValue = (item: PendingItem) => destinationsUsdTotal(item);

    const subtotalBtcValue = (item: PendingItem) => destinationsBtcTotal(item);

    const totalUsdText = (item: PendingItem) => {
      const approx = item.status === "ready" ? "" : "~ ";
      return `${approx}$ ${formatUsd(totalUsdWithFee(item))} USD`;
    };

    const totalBtcText = (item: PendingItem) => {
      const approx = item.status === "ready" ? "" : "~ ";
      return `${approx}${formatBtc(totalBtcWithFee(item))} BTC`;
    };

    const subtotalUsdText = (item: PendingItem) =>
      `$ ${formatUsd(subtotalUsdValue(item))} USD`;

    const subtotalBtcText = (item: PendingItem) =>
      `${formatBtc(subtotalBtcValue(item))} BTC`;

    const amountUsdText = (item: PendingItem, dest: DestinationRow) => {
      const val = amountUsdValue(item, dest);
      if (val === undefined || val === null) return "—";
      const approx =
        dest.currency !== "USD" && item.status !== "ready" ? "~ " : "";
      return `${approx}$ ${formatUsd(val)} USD`;
    };

    const amountBtcText = (item: PendingItem, dest: DestinationRow) => {
      const val = amountBtcValue(item, dest);
      if (val === undefined || val === null) return "—";
      const approx =
        dest.currency !== "BTC" && item.status !== "ready" ? "~ " : "";
      return `${approx}${formatBtc(val)} BTC`;
    };

    const usdAmountClass = (item: PendingItem, dest: DestinationRow) => {
      if (item.status === "ready") return "";
      return dest.currency === "USD" ? "text-dark fw-bold" : "text-blue";
    };

    const btcAmountClass = (item: PendingItem, dest: DestinationRow) => {
      if (item.status === "ready") return "";
      return dest.currency === "BTC" ? "text-dark fw-bold" : "text-blue";
    };

    const currencyBadgeClass = (currency: "USD" | "BTC") =>
      currency === "USD" ? "bg-success" : "bg-warning";

    watch(
      items,
      (list) => {
        list.forEach((item) => {
          if (!selectedSpeed.value[item._id]) {
            selectedSpeed.value[item._id] =
              (item.feeSpeed as FeeSpeed) || "standard";
          }
        });
        if (editingId.value) {
          const current = list.find((row) => row._id === editingId.value);
          if (current) {
            editingValue.value = toFormValue(current);
          } else {
            cancelEdit();
          }
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      withdrawalsStore.listenPendingRequests();
      vault.fetchSummary();
    });

    onBeforeUnmount(() => {
      withdrawalsStore.stopListening();
    });

    return {
      items,
      expanded,
      btcPrice,
      availableBtc,
      isUpdating,
      canAuthorize,
      canEdit,
      toggleExpand,
      isEditing,
      editingValue,
      startEdit,
      cancelEdit,
      saveEdit,
      validateAddress,
      authorize,
      retry,
      reject,
      cancel,
      reopen,
      deleteReq,
      calculateFee,
      sendTransaction,
      isActionLoading,
      statusLabel,
      statusClass,
      statusIcon,
      speedLabel,
      formatDate,
      formatUsd,
      formatBtc,
      feeUsd,
      totalUsdText,
      totalBtcText,
      subtotalUsdText,
      subtotalBtcText,
      amountUsdText,
      amountBtcText,
      usdAmountClass,
      btcAmountClass,
      currencyBadgeClass,
      selectedSpeed,
      amountUsdValue,
      amountBtcValue,
      getApprovalStatus,
    };
  },
});
